import { tv, type VariantProps } from 'tailwind-variants'

export const defaultButtonCommonVariants = tv({
  base: 'web:transition-all web:duration-300 web:ease-in-out duration-300 ease-in-out',
  variants: {
    variant: {
      solid: 'border border-layout-stroke-contrast-low native:border-0',
      faded: 'border border-layout-stroke-contrast-low native:border-0',
      flat: 'border border-layout-stroke-contrast-low native:border-0',
      light: 'border-none',
      bordered: 'border border-layout-stroke-bordered',
    },
    size: {
      xs: 'web:px-2 web:min-w-12 web:h-8 native:text-center text-sm gap-1 rounded-small',
      sm: 'web:px-3.5 web:min-w-16 web:h-10 native:text-center text-md gap-1.5 rounded-small',
      md: 'web:px-4 web:min-w-20 web:h-12 native:text-center text-md gap-2 rounded-medium',
      lg: 'web:px-6 web:min-w-24 web:h-14 native:text-center text-md web:gap-2 rounded-large',
    },
    colors: {
      default: '',
      primary: '',
      secondary: '',
      success: '',
      danger: '',
    },
    contrast: {
      high: '',
      default: '',
      low: '',
    },
    isIconOnly: {
      true: '!px-0 !gap-0 !min-w-0 !aspect-square !w-auto',
      false: '[&>svg]:max-w-[theme(spacing.8)]',
    },
    radius: {
      none: 'rounded-none',
      sm: 'rounded-small',
      md: 'rounded-medium',
      lg: 'rounded-large',
      full: 'rounded-full',
    },
    fullWidth: {
      true: 'w-full',
    },
    shadow: {
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
    },
  },
  defaultVariants: {
    colors: 'primary',
    size: 'lg',
    variant: 'solid',
    shadow: 'none',
    contrast: 'default',
  },
  compoundVariants: [
    {
      variant: 'flat',
      colors: 'primary',
      class: 'bg-primary-bg-light transition-shadow hover:shadow-md',
    },
    {
      variant: 'flat',
      colors: 'secondary',
      class: 'bg-secondary-bg-light transition-shadow hover:shadow-md',
    },
    {
      variant: 'flat',
      colors: 'success',
      class: 'bg-success-bg-light transition-shadow hover:shadow-md',
    },
    {
      variant: 'flat',
      colors: 'danger',
      class: 'bg-danger-bg-light transition-shadow hover:shadow-md',
    },
    {
      variant: 'flat',
      colors: 'default',
      class: 'bg-default-bg-light transition-shadow hover:shadow-md',
    },
    {
      variant: 'bordered',
      colors: 'default',
      class: 'bg-default-bg-white hover:bg-default-bg-light',
    },
    {
      variant: 'solid',
      colors: 'default',
      class: 'bg-default-button-solid hover:bg-default-button-solid-hover',
    },
    {
      variant: 'faded',
      colors: 'default',
      class: 'bg-default-bg-medium hover:bg-default-bg-high',
    },
    {
      variant: 'light',
      colors: 'default',
      class: 'bg-transparent hover:bg-default-bg-medium border-transparent hover:border-default-bg-medium',
    },
    {
      variant: 'solid',
      colors: 'primary',
      class: 'border-none',
    },
    {
      variant: 'faded',
      colors: 'primary',
      class: 'bg-primary-bg-medium hover:bg-primary-bg-high',
    },
    {
      variant: 'light',
      colors: 'primary',
      class: 'bg-transparent hover:bg-primary-bg-medium border-transparent hover:border-primary-bg-medium',
    },
    {
      variant: 'solid',
      colors: 'secondary',
      class: 'bg-secondary-button-solid hover:bg-secondary-button-solid-hover',
    },
    {
      variant: 'faded',
      colors: 'secondary',
      class: 'bg-secondary-bg-medium hover:bg-secondary-bg-high',
    },
    {
      variant: 'light',
      colors: 'secondary',
      class: 'bg-transparent hover:bg-secondary-bg-medium border-transparent hover:border-secondary-bg-medium',
    },
    {
      variant: 'solid',
      colors: 'success',
      class: 'bg-success-button-solid hover:bg-success-button-solid-hover',
    },
    {
      variant: 'faded',
      colors: 'success',
      class: 'bg-success-bg-medium hover:bg-success-bg-high',
    },
    {
      variant: 'light',
      colors: 'success',
      class: 'bg-transparent hover:bg-success-bg-medium border-transparent hover:border-success-bg-medium',
    },
    {
      variant: 'solid',
      colors: 'danger',
      class: 'bg-danger-button-solid hover:bg-danger-button-solid-hover',
    },
    {
      variant: 'faded',
      colors: 'danger',
      class: 'bg-danger-bg-medium hover:bg-danger-bg-high',
    },
    {
      variant: 'light',
      colors: 'danger',
      class: 'bg-transparent hover:bg-danger-bg-medium border-transparent hover:border-danger-bg-medium',
    },
    // Compensate for no border in primary color and faded variant
    {
      size: 'xs',
      colors: 'primary',
      class: '!px-[9px]',
    },
    {
      size: 'sm',
      colors: 'primary',
      class: '!px-[15px]',
    },
    {
      size: 'md',
      colors: 'primary',
      class: '!px-[17px]',
    },
    {
      size: 'lg',
      variant: 'light',
      class: '!px-[25px]',
    },
    {
      size: 'xs',
      variant: 'light',
      class: '!px-[9px]',
    },
    {
      size: 'sm',
      variant: 'light',
      class: '!px-[15px]',
    },
    {
      size: 'md',
      variant: 'light',
      class: '!px-[17px]',
    },
    {
      size: 'lg',
      variant: 'light',
      class: '!px-[25px]',
    },
    // Light & Low Contrast
    {
      variant: 'light',
      colors: 'default',
      contrast: 'low',
      class: 'bg-transparent hover:bg-default-icon-button-bg',
    },
    {
      variant: 'light',
      colors: 'primary',
      contrast: 'low',
      class: 'bg-transparent hover:bg-primary-icon-button-bg',
    },
    {
      variant: 'light',
      colors: 'secondary',
      contrast: 'low',
      class: 'bg-transparent hover:bg-secondary-icon-button-bg',
    },
    {
      variant: 'light',
      colors: 'success',
      contrast: 'low',
      class: 'bg-transparent hover:bg-success-icon-button-bg',
    },
    {
      variant: 'light',
      colors: 'danger',
      contrast: 'low',
      class: 'bg-transparent hover:bg-danger-icon-button-bg',
    },
    {
      isIconOnly: true,
      size: 'xs',
      class: '!h-8 !w-8 !min-w-8',
    },
    {
      isIconOnly: true,
      size: 'sm',
      class: '!h-10 !w-10 !min-w-10',
    },
    {
      isIconOnly: true,
      size: 'md',
      class: '!h-12 !w-12 !min-w-12',
    },
    {
      isIconOnly: true,
      size: 'lg',
      class: '!h-14 !w-14 !min-w-14',
    },
  ],
})

export const defaultButtonTextCommonVariants = tv({
  base: 'font-bold font-nunito text-mdlc text-center web:self-center',
  variants: {
    ...defaultButtonCommonVariants.variants,
    // The text/icon inside the button must not inherit the button's own
    // border/box styling (notably `bordered`, which has no `native:border-0`).
    variant: {
      solid: '',
      faded: '',
      flat: '',
      light: '',
      bordered: '',
    },
    bold: { true: 'font-bold' },
  },
  compoundVariants: [
    {
      variant: 'flat',
      colors: 'primary',
      class: 'text-primary-label-light',
    },
    {
      variant: 'flat',
      colors: 'secondary',
      class: 'text-secondary-label-light',
    },
    {
      variant: 'flat',
      colors: 'danger',
      class: 'text-danger-label-light',
    },
    {
      variant: 'flat',
      colors: 'success',
      class: 'text-success-label-light',
    },
    {
      variant: 'flat',
      colors: 'default',
      class: 'text-default-label-light',
    },

    {
      variant: 'bordered',
      colors: 'default',
      class: 'text-contrast-default',
    },
    {
      variant: 'solid',
      colors: 'default',
      class: 'text-default-label-solid',
    },
    {
      variant: 'solid',
      colors: 'primary',
      class: 'text-primary-label-solid',
    },
    {
      variant: 'solid',
      colors: 'secondary',
      class: 'text-secondary-label-solid',
    },
    {
      variant: 'solid',
      colors: 'success',
      class: 'text-success-label-solid',
    },
    {
      variant: 'solid',
      colors: 'danger',
      class: 'text-danger-label-solid',
    },
    // Faded & Light
    {
      variant: ['faded', 'light'],
      colors: 'default',
      class: 'text-default-label-light',
    },
    {
      variant: ['faded', 'light'],
      colors: 'primary',
      class: 'text-primary-label-light',
    },
    {
      variant: ['faded', 'light'],
      colors: 'secondary',
      class: 'text-secondary-label-light',
    },
    {
      variant: ['faded', 'light'],
      colors: 'success',
      class: 'text-success-label-light',
    },
    {
      variant: ['faded', 'light'],
      colors: 'danger',
      class: 'text-danger-label-light',
    },
    {
      variant: 'light',
      colors: 'default',
      contrast: 'low',
      class: 'text-default-text-contrast-low',
    },
    {
      variant: 'light',
      colors: 'primary',
      contrast: 'low',
      class: 'text-primary-text-contrast-low',
    },
    {
      variant: 'light',
      colors: 'secondary',
      contrast: 'low',
      class: 'text-secondary-text-contrast-low',
    },
    {
      variant: 'light',
      colors: 'success',
      contrast: 'low',
      class: 'text-success-text-contrast-low',
    },
    {
      variant: 'light',
      colors: 'danger',
      contrast: 'low',
      class: 'text-danger-text-contrast-low',
    },
  ],
  defaultVariants: {
    colors: 'primary',
    variant: 'solid',
    shadow: 'none',
  },
})

export type TDefaultButtonCommonVariantProps = VariantProps<typeof defaultButtonCommonVariants>
